import { useRef, useState } from "react";
import { Plus, X, Loader2, UploadCloud } from "lucide-react";
import { cn } from "@/lib/utils";
import { useLanguage } from "@/contexts/LanguageContext";
import { PRODUCT_CREATIVE_MAX_UPLOADS } from "@/config/productCreative";
import type { UploadedSource } from "@/pages/ProductCreative/types";

interface ProductCreativeUploadProps {
  uploads: UploadedSource[];
  isUploading: boolean;
  onAddFiles: (files: File[]) => void;
  onRemove: (id: string) => void;
  variant?: "compact" | "hero";
}

export function ProductCreativeUpload({
  uploads,
  isUploading,
  onAddFiles,
  onRemove,
  variant = "compact",
}: ProductCreativeUploadProps) {
  const { t } = useLanguage();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [isDragging, setIsDragging] = useState(false);

  const slotsLeft = PRODUCT_CREATIVE_MAX_UPLOADS - uploads.length;
  const canAddMore = slotsLeft > 0;
  const isHeroEmpty = variant === "hero" && uploads.length === 0;

  // Upload is local-only — the hook keeps the Files in memory and defers
  // the R2 push to the "Create" action (which carries the anonymous gate).
  // So anyone can open the picker / drop files here without auth.
  const openPicker = () => inputRef.current?.click();

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files ? Array.from(e.target.files) : [];
    if (files.length > 0) onAddFiles(files);
    if (inputRef.current) inputRef.current.value = "";
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };
  const handleDragLeave = () => setIsDragging(false);
  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const files = Array.from(e.dataTransfer.files).filter((f) =>
      f.type.startsWith("image/"),
    );
    if (files.length > 0) onAddFiles(files);
  };

  if (isHeroEmpty) {
    return (
      <>
        <button
          type="button"
          onClick={openPicker}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          disabled={isUploading}
          className={cn(
            "w-full h-full min-h-[260px] md:min-h-[360px] rounded-2xl border-2 border-dashed bg-card/40 flex flex-col items-center justify-center gap-3 px-6 py-8 transition-all duration-200 text-center",
            isDragging
              ? "border-primary bg-primary/5 scale-[1.01]"
              : "border-border hover:border-primary/50 hover:bg-card",
            isUploading && "opacity-60 cursor-wait",
          )}
        >
          {isUploading ? (
            <Loader2 className="w-10 h-10 text-muted-foreground animate-spin" />
          ) : (
            <>
              <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center">
                <UploadCloud className="w-8 h-8 text-primary" />
              </div>
              <div>
                <p className="text-base md:text-lg font-semibold text-foreground">
                  {t.productCreative.heroUploadTitle}
                </p>
                <p className="text-xs md:text-sm text-muted-foreground mt-1">
                  {t.productCreative.heroUploadHint}
                </p>
              </div>
              <span className="inline-flex items-center gap-2 mt-2 px-4 py-2 bg-primary text-primary-foreground rounded-full text-sm font-medium">
                <Plus className="w-4 h-4" />
                {t.productCreative.uploadAdd}
              </span>
              <p className="text-[11px] text-muted-foreground/80 mt-1">
                {t.productCreative.uploadFormats}
              </p>
            </>
          )}
        </button>
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          multiple
          className="hidden"
          onChange={handleChange}
        />
      </>
    );
  }

  return (
    <div>
      {variant === "compact" && (
        <div className="mb-3">
          <h3 className="text-base md:text-lg font-semibold text-foreground">
            {t.productCreative.uploadTitle}
          </h3>
          <p className="text-xs text-muted-foreground mt-0.5">
            {t.productCreative.uploadHint}
          </p>
        </div>
      )}

      <div className="grid grid-cols-3 gap-3 md:gap-4">
        {uploads.map((upload) => (
          <div
            key={upload.id}
            className="relative aspect-square rounded-2xl overflow-hidden border border-border bg-card"
          >
            <img
              src={upload.previewUrl}
              alt={t.productCreative.uploadTitle}
              className="absolute inset-0 w-full h-full object-cover"
            />
            <button
              type="button"
              onClick={() => onRemove(upload.id)}
              aria-label={t.productCreative.removePhoto}
              className="absolute top-2 end-2 w-7 h-7 bg-background/90 hover:bg-background rounded-full flex items-center justify-center shadow-md"
            >
              <X className="w-4 h-4 text-foreground" />
            </button>
          </div>
        ))}

        {canAddMore && (
          <button
            type="button"
            onClick={openPicker}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            disabled={isUploading}
            className={cn(
              "aspect-square rounded-2xl border-2 border-dashed bg-card flex flex-col items-center justify-center gap-2 transition-all duration-200",
              isDragging
                ? "border-primary bg-primary/5"
                : "border-border hover:border-primary/40 hover:bg-card",
              isUploading && "opacity-60",
            )}
          >
            {isUploading ? (
              <Loader2 className="w-6 h-6 text-muted-foreground animate-spin" />
            ) : (
              <>
                <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
                  <Plus className="w-5 h-5 text-primary" />
                </div>
                <span className="text-xs text-muted-foreground">
                  {t.productCreative.uploadAdd}
                </span>
              </>
            )}
          </button>
        )}
      </div>

      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        multiple
        className="hidden"
        onChange={handleChange}
      />
    </div>
  );
}
